import { apiClient } from './client'
import i18n from '@/i18n/config'
import { API_ENDPOINTS } from '@/utils/constants'
import type {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  ActivateCabinetRequest,
  CabinetInvitationPreview,
} from '@/types'

import type { User } from '@/types'

interface LoginApiResponse {
  success: boolean
  message: string
  data?: {
    user: User
    token: string
    token_type: string
    expires_in: number
  }
}

/**
 * Подтянуть параметры эффективной подписки и слить в объект пользователя (отдельный GET от `/profile`).
 *
 * `token` — только что выданный JWT, если он ещё не сохранён в сторе (вход, регистрация).
 * Без него запрос ушёл бы без авторизации, получил 401, и интерсептор разлогинил бы
 * пользователя: после регистрации ученика выбрасывало на страницу входа.
 */
export async function mergeSubscriptionParameters(user: User, token?: string): Promise<User> {
  try {
    const params = await authApi.getSubscriptionParameters(token)
    return { ...user, subscription_parameters: params }
  } catch {
    return { ...user, subscription_parameters: user.subscription_parameters ?? {} }
  }
}

export const authApi = {
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    const response = await apiClient.post<LoginApiResponse>(
      API_ENDPOINTS.AUTH.LOGIN,
      credentials
    )
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Ошибка авторизации')
    }

    return {
      user: response.data.data.user,
      token: response.data.data.token,
      token_type: response.data.data.token_type,
      expires_in: response.data.data.expires_in,
    }
  },

  refresh: async (): Promise<{ token: string; expires_in: number }> => {
    interface RefreshApiResponse {
      success: boolean
      message: string
      data?: {
        token: string
        expires_in: number
      }
    }

    const response = await apiClient.post<RefreshApiResponse>(API_ENDPOINTS.AUTH.REFRESH)

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Ошибка обновления токена')
    }

    return {
      token: response.data.data.token,
      expires_in: response.data.data.expires_in,
    }
  },

  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const response = await apiClient.post<LoginApiResponse>(
      API_ENDPOINTS.AUTH.REGISTER,
      data
    )
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Ошибка регистрации')
    }

    return {
      user: response.data.data.user,
      token: response.data.data.token,
      token_type: response.data.data.token_type,
      expires_in: response.data.data.expires_in,
    }
  },

  /**
   * Приглашение в кабинет по коду из ссылки — публично, без токена.
   * Неизвестный код — 404.
   */
  getCabinetInvitation: async (code: string): Promise<CabinetInvitationPreview> => {
    const response = await apiClient.get<{ success?: boolean; data: CabinetInvitationPreview }>(
      API_ENDPOINTS.AUTH.CABINET_INVITATION(code),
    )

    return response.data.data
  },

  /**
   * Задать пароль по ссылке и войти — ответ как у регистрации.
   * Отказы — 422 с `error`: `invitation_expired`, `invitation_used`, `email_taken`.
   */
  activateCabinet: async (code: string, data: ActivateCabinetRequest): Promise<AuthResponse> => {
    const response = await apiClient.post<LoginApiResponse>(
      API_ENDPOINTS.AUTH.CABINET_INVITATION_ACTIVATE(code),
      data,
    )

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || i18n.t('cabinetActivation.error'))
    }

    return {
      user: response.data.data.user,
      token: response.data.data.token,
      token_type: response.data.data.token_type,
      expires_in: response.data.data.expires_in,
    }
  },

  logout: async (): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT)
  },

  forgotPassword: async (email: string): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email })
  },

  resetPassword: async (payload: {
    token: string
    email: string
    password: string
    password_confirmation: string
  }): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, payload)
  },

  /**
   * Сменить пароль из профиля.
   *
   * При `logout_other_sessions` бэкенд инвалидирует все ранее выданные токены,
   * включая токен текущего запроса, и возвращает свежий — вызывающая сторона
   * обязана положить его в стор, иначе следующий запрос получит 401.
   */
  changePassword: async (payload: {
    current_password: string
    new_password: string
    new_password_confirmation: string
    logout_other_sessions: boolean
  }): Promise<{ token: string; expires_in: number } | null> => {
    interface ChangePasswordApiResponse {
      success: boolean
      message: string
      data?: {
        token?: string
        expires_in?: number
      }
    }

    const response = await apiClient.post<ChangePasswordApiResponse>(
      API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
      payload,
    )

    const token = response.data.data?.token
    if (!token) {
      return null
    }

    return { token, expires_in: response.data.data?.expires_in ?? 0 }
  },

  getSubscriptionParameters: async (token?: string): Promise<Record<string, string>> => {
    interface SubscriptionParametersResponse {
      success?: boolean
      data?: Record<string, string>
    }
    const response = await apiClient.get<SubscriptionParametersResponse>(
      API_ENDPOINTS.AUTH.SUBSCRIPTION_PARAMETERS,
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
    )
    if (response.data?.data && typeof response.data.data === 'object') {
      return response.data.data
    }
    return {}
  },


  getProfile: async (): Promise<User> => {
    interface ProfileResponse {
      data?: User
      id?: number
      message?: string
    }
    const response = await apiClient.get<ProfileResponse>(API_ENDPOINTS.AUTH.PROFILE)
    
    if (response.data?.data) {
      return response.data.data
    }

    if (response.data && response.data.id) {
      return response.data as User
    }

    throw new Error(response.data?.message || 'Ошибка загрузки профиля')
  },
}
